import apiClient from "./apiClient";
import { getOrganizationId } from "../utils/organizationUtils";
import {
  buildSampleCollectedId,
  buildSampleTransactionPayload,
  canBuildSampleCollectedId,
  canUpsertSampleTransaction,
  getSampleReportId,
} from "../utils/sampleTransactionUtils";

const API_URL = `/sample-transactions`;

const isMissingSampleTransactionApi = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 501 || status === 405;
};

/**
 * Route to {orgId}_sample_transactions the same way as pathology-transactions:
 * query ?organizationId= plus header. Do not rely on body organizationId.
 */
const getOrgRoutingConfig = () => {
  const organizationId = getOrganizationId();
  if (!organizationId) return {};
  return {
    params: { organizationId },
    headers: {
      organizationId,
      "X-Organization-Id": organizationId,
    },
  };
};

const toList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  if (data && typeof data === "object") return [data];
  return [];
};

const resolveInvoiceId = async (payload) => {
  if (payload?.invoiceId) return payload.invoiceId;
  try {
    const { getInvoiceList } = await import("./invoiceService.js");
    const { findPathologyTestInvoiceId } = await import(
      "../utils/pathTransactionHistoryUtils.js"
    );
    const invoices = await getInvoiceList({ fullRange: true }).catch(() => []);
    return findPathologyTestInvoiceId(invoices, {
      invoiceId: payload.invoiceId,
      prescriptionId: payload.prescriptionId,
      patientId: payload.patientId,
    });
  } catch (_) {
    return null;
  }
};

const hasLabResultText = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== "" && text.toLowerCase() !== "null";
};

const isCompleteResultStatus = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  return normalized === "COMPLETE" || normalized === "COMPLETED";
};

export const upsertSampleTransaction = async (transactionData) => {
  if (!canUpsertSampleTransaction(transactionData)) {
    return null;
  }

  const payload = buildSampleTransactionPayload(transactionData);
  // Guarantee YES/NO strings for result upserts (never lab-result text).
  if (
    payload.resultEntered === "YES" ||
    payload.resultFlag === "YES" ||
    payload.hasResult === "YES" ||
    transactionData?.resultEntered === true ||
    transactionData?.resultEntered === "YES" ||
    transactionData?.resultFlag === true ||
    transactionData?.resultFlag === "YES"
  ) {
    payload.result = "YES";
    payload.resultEntered = "YES";
    payload.hasResult = "YES";
    payload.resultFlag = "YES";
  }
  if (!payload.invoiceId) {
    const invoiceId = await resolveInvoiceId(payload);
    if (invoiceId) {
      payload.invoiceId = invoiceId;
      payload.invoice_id = invoiceId;
      if (payload.billed === "NO") {
        payload.billed = "YES";
        payload.bill = "YES";
      }
    }
  }

  try {
    const response = await apiClient.put(`${API_URL}/upsert`, payload, getOrgRoutingConfig());
    return response.data;
  } catch (putError) {
    if (!isMissingSampleTransactionApi(putError)) {
      console.warn(
        "[sampleTransactionService] Upsert failed:",
        putError?.response?.status || putError?.message
      );
    }
    try {
      const response = await apiClient.post(API_URL, payload, getOrgRoutingConfig());
      return response.data;
    } catch (postError) {
      if (!isMissingSampleTransactionApi(postError)) {
        console.warn(
          "[sampleTransactionService] Sample transaction not saved:",
          postError?.response?.status || postError?.message
        );
      }
      return null;
    }
  }
};

/**
 * Insert/update rows at sample collection. Never throws — collection
 * status updates must still succeed if this table/API is missing.
 */
export const recordSamplesCollected = async (rows = [], extras = {}) => {
  const list = Array.isArray(rows) ? rows : [rows];
  const saved = [];
  const ids = [];

  for (const row of list) {
    if (!row) continue;
    const payload = {
      ...row,
      ...extras,
      sampleCollected: true,
      result: row.result ?? false,
    };
    if (!canBuildSampleCollectedId(payload) && !payload.uniqueSampleCollectedId) {
      continue;
    }
    const uniqueSampleCollectedId = buildSampleCollectedId(payload);
    ids.push(uniqueSampleCollectedId);
    const result = await upsertSampleTransaction(payload);
    if (result) saved.push(result);
  }

  return { saved, uniqueSampleCollectedIds: [...new Set(ids)] };
};

/**
 * Mark result entered (and report id) after add/update. Never throws.
 * Always forces result=YES and keeps reportId on the upsert payload.
 * Backend can match an existing row by report_id when unique id is omitted.
 *
 * Important: never put lab-result *text* into `result` — that field is YES/NO only.
 */
export const recordSampleResultEntered = async (row = {}, extras = {}) => {
  const reportId = row.reportId ?? row.report_id ?? extras.reportId ?? extras.report_id ?? null;
  // Strip any lab-result text / accidental `result` so payload builder cannot confuse it.
  const {
    result: _rowResult,
    resultStatus: _rowStatus,
    testResult: _rowTestResult,
    ...rowRest
  } = row || {};
  const {
    result: _extraResult,
    resultStatus: _extraStatus,
    testResult: _extraTestResult,
    ...extrasRest
  } = extras || {};

  const payload = {
    ...rowRest,
    ...extrasRest,
    sampleCollected: row.sampleCollected ?? extras.sampleCollected ?? true,
    // Force YES/NO flag fields only (backend merge looks at result / resultEntered).
    result: "YES",
    resultFlag: "YES",
    hasResult: "YES",
    resultEntered: "YES",
    reportId,
    report_id: reportId,
  };
  if (!canUpsertSampleTransaction(payload)) {
    console.warn(
      "[sampleTransactionService] Skipping result upsert — missing identifiers",
      {
        patientId: payload.patientId,
        labTestId: payload.labTestId,
        reportId: getSampleReportId(payload),
      }
    );
    return null;
  }
  return upsertSampleTransaction(payload);
};

/**
 * Best-effort sync after OPD path lab report result save.
 * Called from pathLabTestReportService.updateReportResult (and updateReport).
 */
export const syncSampleTransactionAfterResult = async (
  reportId,
  reportData = {},
  responseData = {},
  meta = {}
) => {
  if (!reportId) return null;

  const status = reportData?.resultStatus ?? meta?.resultStatus;
  const resultText = reportData?.result ?? meta?.result;
  const shouldSync =
    isCompleteResultStatus(status) || hasLabResultText(resultText);
  if (!shouldSync) return null;

  try {
    return await recordSampleResultEntered({
      reportId: Number(reportId),
      patientId:
        meta?.patientId ??
        responseData?.patientId ??
        responseData?.patient_id ??
        reportData?.patientId ??
        reportData?.patient_id,
      labTestId:
        meta?.labTestId ??
        responseData?.labTestId ??
        responseData?.testId ??
        responseData?.test_id ??
        responseData?.lab_test_id ??
        reportData?.testId ??
        reportData?.labTestId,
      prescriptionId:
        meta?.prescriptionId ??
        responseData?.prescriptionId ??
        responseData?.prescription_id,
      admissionId:
        meta?.admissionId ??
        responseData?.admissionId ??
        responseData?.admission_id,
      patientType: meta?.patientType ?? "opd",
      patientName: meta?.patientName,
      labTestName: meta?.labTestName ?? meta?.testName,
      invoiceId: meta?.invoiceId ?? meta?.invoice_id,
      uniqueSampleCollectedId: meta?.uniqueSampleCollectedId,
      billed: meta?.billed ?? true,
      sampleCollected: true,
    });
  } catch (error) {
    console.warn(
      "[sampleTransactionService] syncSampleTransactionAfterResult failed:",
      error?.message || error
    );
    return null;
  }
};

/**
 * Best-effort sync after IPD lab test result save.
 * Called from PatientlabTestService.updateResults.
 */
export const syncSampleTransactionAfterIpdResult = async (item = {}, responseData = {}) => {
  const resultText = item?.testResult ?? responseData?.testResult;
  const status = item?.testStatus ?? responseData?.testStatus;
  const shouldSync =
    isCompleteResultStatus(status) || hasLabResultText(resultText);
  if (!shouldSync) return null;

  const reportId =
    item?.reportId ??
    item?.report_id ??
    responseData?.reportId ??
    responseData?.report_id ??
    null;

  try {
    return await recordSampleResultEntered({
      reportId: reportId != null ? Number(reportId) : null,
      patientId: item?.patientId ?? responseData?.patientId,
      labTestId: item?.testId ?? item?.labTestId ?? responseData?.testId,
      prescriptionId:
        item?.prescriptionId ??
        item?.ipdPrescriptionId ??
        item?.ipd_prescription_id,
      admissionId: item?.admissionId ?? responseData?.admissionId,
      patientType: "ipd",
      patientName: item?.patientName,
      labTestName: item?.labTestName ?? item?.testName ?? item?.resolvedTestName,
      invoiceId: item?.invoiceId ?? item?.invoice_id,
      billed: Boolean(item?.invoiceId ?? item?.invoice_id),
      sampleCollected: true,
    });
  } catch (error) {
    console.warn(
      "[sampleTransactionService] syncSampleTransactionAfterIpdResult failed:",
      error?.message || error
    );
    return null;
  }
};

export const getSampleTransactions = async () => {
  try {
    const response = await apiClient.get(API_URL, getOrgRoutingConfig());
    return toList(response.data);
  } catch (error) {
    if (!isMissingSampleTransactionApi(error)) {
      console.warn("[sampleTransactionService] GET failed:", error?.message);
    }
    return [];
  }
};

export const getSampleTransactionsByPatientId = async (patientId) => {
  try {
    const response = await apiClient.get(`${API_URL}/patient/${patientId}`, getOrgRoutingConfig());
    return toList(response.data);
  } catch (error) {
    if (!isMissingSampleTransactionApi(error)) {
      console.warn("[sampleTransactionService] GET by patient failed:", error?.message);
    }
    return [];
  }
};

export const isSampleTransactionApiUnavailable = isMissingSampleTransactionApi;
